// ACTIONS → Remove Component on the aircraft Components tab (component editor, 2026-10-07;
// Aaron: "need a remove component - copy from legacy"). The shared Sheet slide-in (BP-89)
// carries legacy's removal popup (mod_aircraft/js/aircraft_component_removal.js:574-845);
// "Review" asks the API what the removal WILL do (the dry-run preview) and a confirm dialog
// says so before anything is written. Rules in ./componentLifecycle.ts.
//
// A removal for overhaul (17) offers legacy's "Process Overhaul Job" (removal.js:695-719): a new job
// or one of the aircraft's open overhaul jobs (to_stock 3), with the installation task and outwork.
// The API writes it in the removal's one transaction and needs jobs Edit for it.
//
// Legacy defaults: condition from aircraft.removed_component_(un)serviceable_condition, location
// from parts.items_default_location, store from parts.removed_component_store (:757-811); the
// Date of Values is the aircraft's own, else today (:880-885).
import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, FormControlLabel, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { axios } from "../api";
import AlertDialog from "../components/AlertDialog";
import { ActionButton, CancelButton } from "../components/Buttons";
import Sheet, { SheetActions, SheetContent, SheetHeader } from "../components/Sheet";
import TenantDatePicker from "../components/TenantDatePicker";
import { useConfig } from "../hooks/useConfig";
import { useLocationOptions } from "../hooks/useLocationOptions";
import { usePartsConditionOptions } from "../hooks/usePartsConditionOptions";
import { usePartsStoreOptions } from "../hooks/usePartsStoreOptions";
import { useWorkOrderTerm } from "../hooks/useWorkOrderTerm";
import { jobLabel, useWithSelectedTargets } from "./ComponentWithSelected";
import {
  OVERHAUL_REMOVAL,
  overhaulJobOptions,
  REMOVAL_TYPES,
  RemovalFormValues,
  overhaulJobs,
  RemovalSummary,
  readingAxes,
  removalConfirmLines,
  removalFormErrors,
  removalPayload,
  todayIso,
} from "./componentLifecycle";

export interface RemovableComponent {
  itemId: number;
  partNumber: string | null;
  serial: string | null;
  basedOns: Array<string | null | undefined>;
}

interface Props {
  open: boolean;
  aircraft: { id: number; registration?: string | null; date_of_values?: string | null };
  component: RemovableComponent;
  onClose: () => void;
  onRemoved: () => void;
}

const cfg = (c: any, key: string) => (c ? c[key] ?? c[`aircraft_${key}`] ?? c[`parts_${key}`] : undefined);
const day = (raw?: string | null) => (raw && /^\d{4}-\d{2}-\d{2}/.test(raw) && !raw.startsWith("0000") ? raw.slice(0, 10) : "");

const ComponentRemoveSheet: React.FC<Props> = ({ open, aircraft, component, onClose, onRemoved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const aircraftConfig = useConfig("aircraft");
  const partsConfig = useConfig("parts");
  const locationOptions = useLocationOptions();
  const conditionOptions = usePartsConditionOptions(partsConfig);
  const storeOptions = usePartsStoreOptions(partsConfig);
  const axes = useMemo(() => readingAxes(component.basedOns), [component.basedOns]);
  const term = useWorkOrderTerm();

  const [values, setValues] = useState<RemovalFormValues>(() => ({
    remove_status: "16",
    reason: "",
    condition: "",
    location: "",
    store: "",
    shelf: "",
    date_of_values: day(aircraft.date_of_values) || todayIso(),
    readings: {},
    overhaul_job: false,
    overhaul_job_id: "new",
    overhaul_options: [],
  }));
  const forOverhaul = values.remove_status === OVERHAUL_REMOVAL;
  const { jobs } = useWithSelectedTargets(aircraft.id, forOverhaul, 0);
  const jobChoices = useMemo(() => overhaulJobs(jobs), [jobs]);
  const [errors, setErrors] = useState<Partial<Record<keyof RemovalFormValues, string>>>({});
  const [summary, setSummary] = useState<RemovalSummary | null>(null);
  const [busy, setBusy] = useState(false);

  // legacy's defaults, once the configs are in, without overwriting what was typed. The effect
  // keys on the default VALUES (strings), so a config object's identity never re-runs it.
  const serviceable = values.remove_status === "16";
  const defaultCondition = String(
    cfg(aircraftConfig, serviceable ? "removed_component_serviceable_condition" : "removed_component_unserviceable_condition") ??
      cfg(partsConfig, "removed_component_condition") ??
      ""
  );
  const defaultLocation = String(cfg(partsConfig, "items_default_location") ?? "");
  const defaultStore = String(cfg(partsConfig, "removed_component_store") ?? "");
  useEffect(() => {
    setValues((v) =>
      v.condition && v.location && v.store
        ? v
        : {
            ...v,
            condition: v.condition || defaultCondition,
            location: v.location || defaultLocation,
            store: v.store || defaultStore,
          }
    );
  }, [defaultCondition, defaultLocation, defaultStore]);

  const set = (key: keyof RemovalFormValues, value: any) => setValues((v) => ({ ...v, [key]: value }));

  const review = () => {
    const found = removalFormErrors(values);
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    axios
      .post(`/v6/aircraft/${aircraft.id}/components/${component.itemId}/remove/preview`, removalPayload(values), {
        cache: false,
      })
      .then((resp) => setSummary(resp.data as RemovalSummary))
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        enqueueSnackbar(typeof detail === "string" ? detail : "Could not check the removal", { variant: "error" });
      })
      .finally(() => setBusy(false));
  };

  const confirm = () => {
    setBusy(true);
    axios
      .post(`/v6/aircraft/${aircraft.id}/components/${component.itemId}/remove`, removalPayload(values), { cache: false })
      .then((resp) => {
        const job = (resp?.data as RemovalSummary & { overhaul_job?: { job_id?: number } })?.overhaul_job;
        enqueueSnackbar(
          `${values.remove_status === "18" ? "Component retired" : "Component removed"}${job?.job_id ? `; overhaul job #${job.job_id}` : ""}`,
          { variant: "success" }
        );
        setSummary(null);
        onRemoved();
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        enqueueSnackbar(typeof detail === "string" ? detail : "The component was NOT removed", { variant: "error" });
        setSummary(null);
      })
      .finally(() => setBusy(false));
  };

  return (
    <Sheet open={open} onClose={onClose}>
      <SheetHeader
        onClose={onClose}
        title={`Remove ${component.partNumber ?? "Component"} ${component.serial ? `S/N ${component.serial}` : ""}`.trim()}
      />
      <SheetContent>
        {/* No .mui-form here: that is a record tab's padded card (50px a side), and inside a
            quarter-width sheet it left the fields ~150px ("Reason for re…", "Loca…"; C-GQZV
            walk D8). SheetContent owns the sheet's padding. */}
        <Grid container spacing={2} data-testid="component-remove-form">
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Removal type"
              value={values.remove_status}
              onChange={(e) => set("remove_status", e.target.value)}
            >
              {REMOVAL_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {forOverhaul && (
            <Grid item xs={12} data-testid="component-remove-overhaul">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(values.overhaul_job)}
                    onChange={(e) => set("overhaul_job", e.target.checked)}
                  />
                }
                label={`Raise overhaul ${term.shortSingular}`}
              />
              {values.overhaul_job && (
                <Grid container spacing={1} sx={{ pl: 4 }}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={`Overhaul ${term.shortSingular}`}
                      value={values.overhaul_job_id ?? "new"}
                      onChange={(e) => set("overhaul_job_id", e.target.value)}
                    >
                      <MenuItem value="new">{`Create new ${term.shortSingular}`}</MenuItem>
                      {jobChoices.map((j) => (
                        <MenuItem key={j.id} value={String(j.id)}>
                          {jobLabel(j, term)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {overhaulJobOptions(term).map((o) => (
                    <Grid item xs={12} key={o.value}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={(values.overhaul_options ?? []).includes(o.value)}
                            onChange={(e) =>
                              set(
                                "overhaul_options",
                                e.target.checked
                                  ? [...(values.overhaul_options ?? []), o.value]
                                  : (values.overhaul_options ?? []).filter((x) => x !== o.value)
                              )
                            }
                          />
                        }
                        label={o.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              label="Reason for removal"
              value={values.reason}
              onChange={(e) => set("reason", e.target.value)}
              error={Boolean(errors.reason)}
              helperText={errors.reason}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Condition" value={values.condition} onChange={(e) => set("condition", e.target.value)}>
              {(conditionOptions ?? []).map((o: any) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              required
              label="Location"
              value={values.location}
              onChange={(e) => set("location", e.target.value)}
              error={Boolean(errors.location)}
              helperText={errors.location}
            >
              {(locationOptions ?? []).map((o: any) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Store" value={values.store} onChange={(e) => set("store", e.target.value)}>
              {(storeOptions ?? []).map((o: any) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Shelf" value={values.shelf} onChange={(e) => set("shelf", e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TenantDatePicker
              label="Date of Values"
              required
              fullWidth
              value={values.date_of_values}
              onChange={(iso) => set("date_of_values", iso)}
              error={Boolean(errors.date_of_values)}
              helperText={errors.date_of_values}
            />
          </Grid>
          {axes.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Readings at the Date of Values — leave blank to use the aircraft's current reading
              </Typography>
            </Grid>
          )}
          {axes.map((axis) => (
            <Grid item xs={6} key={axis}>
              <TextField
                fullWidth
                type="number"
                label={axis}
                value={values.readings[axis] ?? ""}
                onChange={(e) => set("readings", { ...values.readings, [axis]: e.target.value })}
              />
            </Grid>
          ))}
        </Grid>
      </SheetContent>
      <SheetActions>
        <CancelButton onClick={onClose} />
        <ActionButton onClick={busy ? () => {} : review}>Review removal</ActionButton>
      </SheetActions>
      <AlertDialog
        open={summary !== null}
        title={values.remove_status === "18" ? "Retire this component?" : "Remove this component?"}
        handleClose={() => setSummary(null)}
        content={
          <div data-testid="component-remove-confirm">
            {summary
              ? removalConfirmLines(summary, aircraft.registration, term).map((line) => (
                  <Typography key={line} variant="body2" sx={{ mb: 0.5 }}>
                    {line}
                  </Typography>
                ))
              : null}
          </div>
        }
      >
        <CancelButton onClick={() => setSummary(null)} />
        <ActionButton onClick={busy ? () => {} : confirm}>{values.remove_status === "18" ? "Retire" : "Remove"}</ActionButton>
      </AlertDialog>
    </Sheet>
  );
};

export default ComponentRemoveSheet;
